import React, { useEffect, useState } from "react";
import { connect, useSelector } from "react-redux";
import { createStructuredSelector } from "reselect";
import { changeTab, fetchModesRequest } from "containers/ChannelsPage/actions";
import { selectModesError } from "containers/ChannelsPage/selectors";
import { InlineNote } from "./InlineNote";

import {
  FileWrapper,
} from "components/InputField/styledComponents";
import Button from "components/Button";
import Divider from "components/Divider";
import { PhotoFilterPreview } from "./components";
import {
  GifStyleWrapper,
  PhotoOptionsWrapper,
  GifStyleFieldLabel,
  StyledForm,
  RadioInputWrapper,
  UpgradeText,
  FieldHead,
} from "./styledComponents";
import Icon from "components/Icon";
import Tooltip from "../../../components/Tooltip";
import { Controller, useWatch } from "react-hook-form";
import { useFeatureFlag } from "utils/posthog";
import {
  isSaveDisabled,
  saveBlockReason,
  sectionHelperText,
  selectedModeShortnames,
  visibleImageStyles,
  singleSelectStyleValue,
  singleSelectStylePatch,
  singleSelectModePatch,
  HEADSHOT_STYLE_VALUE,
  UI_STYLE_BNW,
  mapUiStyleToWire,
  mapWireStyleToUi,
  aiModeSelected,
  videoModeSelected,
  consentAckBlocksSave,
} from "./photoOptionsState";
import BipaConsentNotice from "./BipaConsentNotice";

/*
 * Per-variant BIPA-consent gate derivation (pure). `selectedShortnamesNow` is
 * resolved by the CALLER from its flag-filtered visibleCaptureModes, so with
 * the AI flag off `aiSelected` stays false (even on an AI-persisted channel)
 * and the flag-off path renders byte-identically to before. `aiPersisted` and
 * `consentAcked` are owned ABOVE the variant switch (see PhotoOptionsForm): the
 * persisted verdict comes from the channel's server-derived initialValues, never
 * from live form state, and the acknowledgment survives a picker-variant flip
 * so neither can be spoofed by a remount.
 */
const bipaConsentGate = (selectedShortnamesNow, aiPersisted, consentAcked) => {
  const aiSelected = aiModeSelected(selectedShortnamesNow);
  return {
    aiSelected,
    consentBlocked: consentAckBlocksSave({
      aiSelected,
      aiPersisted,
      acked: consentAcked,
    }),
  };
};

// PostHog flag that gates the multi-select capture-mode + image-style UI.
// When disabled (the default until rolled out), the legacy single-select
// (radio-button) UI renders and channels are saved with the legacy
// scalar fields only. When enabled, the multi-select (checkbox) UI renders
// and arrays of enabled_mode_ids / enabled_image_styles are sent on save.
//
// The flag is rolled out coordinated with iOS readiness: in-field iPads on
// pre-multi-mode builds cannot interpret a channel that exposes more than
// one mode, so the flag stays off until the iOS multi-mode UI catches up.
export const MULTI_SELECT_FLAG = "multi_select_picker_enabled";

// PostHog flag that gates the AI capture mode + its Professional Headshot image
// style. Independent of MULTI_SELECT_FLAG: when this is on, an "AI" tile is
// added to the capture-mode picker (in whichever picker variant is active), and
// selecting AI reduces the image styles to Professional Headshot. Default OFF /
// fail-closed until the AI headshot pipeline ships to a channel's environment.
export const AI_MODE_FLAG = "ai_capture_mode_enabled";

// PostHog flag that gates the Story Video capture tile. Superadmins see the
// tile regardless of the flag. Default OFF / fail-closed.
export const STORY_VIDEO_FLAG = "story_video_enabled";

const modeIsVisible = (mode, { aiEnabled, videoEnabled }) =>
  !(mode.aiMode && !aiEnabled) && !(mode.videoMode && !videoEnabled);

// UI vocabulary tokens are kept stable; the "bnw" UI ↔ "bw" wire mapping lives
// in photoOptionsState (`mapUiStyleToWire` / `mapWireStyleToUi`).

export const captureModes = [
  {
    shortname: "4pose",
    label: "Classic GIF",
    description:
      "Classic GIF mode captures four distinct photos, giving your guests a few seconds to change up their pose between each one, then animates them into a loop where each photo plays in order.",
    value: (data) => {
      var value = Object.values(data).filter(
        (val) => val.shortname === "4pose"
      )[0];
      return value ? value.id : 1;
    },
  },
  {
    shortname: "tnb",
    label: "Boomerang GIF",
    description:
      "Boomerang GIF mode captures about 1 second of real-time video of your guests, then animates it into a loop that plays their video forward and then in reverse",
    value: (data) => {
      var value = Object.values(data).filter(
        (val) => val.shortname === "tnb"
      )[0];
      return value ? value.id : 2;
    },
  },
  {
    shortname: "still",
    label: "Still",
    description:
      "Still mode takes the move out of Movebooth and captures a single photo of your guests.",
    value: (data) => {
      var value = Object.values(data).filter(
        (val) => val.shortname === "still"
      )[0];
      return value ? value.id : 3;
    },
  },
  {
    shortname: "video",
    label: "Story Video",
    description:
      "Records a short video clip with sound. Guests get a video instead of photos or GIFs.",
    // `videoMode` marks this tile so the render sites can hide it behind the
    // story_video_enabled flag (or a superadmin) without mutating this const.
    videoMode: true,
    value: (data) => {
      var value = Object.values(data).filter(
        (val) => val.shortname === "video"
      )[0];
      return value ? value.id : 4;
    },
  },
  {
    shortname: "ai",
    label: "AI",
    description:
      "AI mode captures your guests in four framed poses, then transforms them into polished, professional headshots that are delivered to each guest.",
    // `aiMode` marks this tile so the render sites can hide it behind the
    // ai_capture_mode_enabled flag without mutating this const.
    aiMode: true,
    value: (data) => {
      var value = Object.values(data).filter(
        (val) => val.shortname === "ai"
      )[0];
      // Agreed cross-lane fallback id until rails seeds the `ai` mode; react
      // reads the real id from /v2/modes once it lands.
      return value ? value.id : 5;
    },
  },
];

// Each style declares the capture-mode shortnames it belongs to via `modes`.
// The classic modes share the standard/bnw/glitch styles; the AI mode offers
// only Professional Headshot. The image-style section filters by the selected
// capture mode(s) so the two style families never mix in the picker.
// Story Video shares the classic styles (it is a captured clip, filtered like
// one); without this a video-only channel has no style and cannot be saved.
const CLASSIC_MODES = ["4pose", "tnb", "still", "video"];

const imageStyles = [
  {
    shortname: "tnb",
    label: "Standard",
    description:
      "The Standard image style will not apply any effects to your photos.",
    modes: CLASSIC_MODES,
    value: (_data) => "standard",
  },
  {
    shortname: "bnw",
    label: "Black & White",
    description:
      "The Black & White image style will apply an attractive and contrasty black-and-white color palette to your photos",
    modes: CLASSIC_MODES,
    value: (_data) => "bnw",
  },
  {
    shortname: "glitch",
    label: "Glitch",
    description:
      "The Glitch image style will make your photos black & white and apply an anaglyph 3d-like effect to them–reminiscent of old 3d glasses that used red and blue lenses–to provide a glitchy look to your photos",
    modes: CLASSIC_MODES,
    value: (_data) => "glitch",
  },
  {
    shortname: "headshot",
    label: "Professional Headshot",
    description:
      "Professional Headshot restyles your guests' AI-mode captures into polished, studio-quality headshots in a range of professional looks.",
    modes: ["ai"],
    // The `ai` sub-object is the extensibility seam for future AI style kinds
    // (guest_choose / custom_prompt / reference). Only `preset` is built for the
    // prototype; the shape is here so later kinds slot in without a new branch.
    ai: { kind: "preset" },
    value: (_data) => HEADSHOT_STYLE_VALUE,
  },
];

// === Multi-select (flag ON) variant ============================================

const MultiSelectGifStyleField = ({
  label,
  selectedTheme,
  themeInstance,
  value,
  shortname,
  description,
  control,
  isSelected,
  isLastSelected,
  onToggle,
  inputName,
}) => {
  const values = useWatch({ control: control });
  const firstLine = values["first_line_text"];
  const secondLine = values["second_line_text"];
  const withGraphic = values["with_photo_filter_graphic"];
  const withFilter = values["with_photo_filter"];
  const handleChange = (e) => {
    // Prevent the last-checked tile from being toggled off — at least one
    // mode/style must always remain selected per task spec.
    if (!e.target.checked && isLastSelected) {
      e.preventDefault();
      e.target.checked = true;
      return;
    }
    onToggle(value, e.target.checked);
  };
  return (
    <GifStyleFieldLabel className={isSelected ? "selected" : ""}>
      <PhotoFilterPreview
        med
        selectedTheme={selectedTheme}
        themeInstance={themeInstance}
        noMargin={true}
        center={true}
        photoOptions={shortname}
        firstLine={firstLine}
        secondLine={secondLine}
        withGraphic={withGraphic}
        withFilter={withFilter}
      />
      <RadioInputWrapper>
        <div className=" text-mb-body flex items-center gap-3 pl-1 py-.05">
          <input
            type="checkbox"
            name={inputName}
            value={value}
            checked={isSelected}
            onChange={handleChange}
            aria-label={label}
          />
          <label>{label}</label>
        </div>
        <Tooltip
          offset="-10px"
          width="200px"
          position="bottom"
          text={description}
        >
          <Icon size="12" name="help" />
        </Tooltip>
      </RadioInputWrapper>
    </GifStyleFieldLabel>
  );
};

const MultiSelectPhotoOptionsSection = ({
  groupName,
  header,
  description,
  // The already-filtered definitions to render (capture modes gated by the AI
  // flag, image styles gated by the selected capture mode). Resolved by the
  // parent so this section stays presentational.
  itemDefs,
  modes,
  selectedTheme,
  themeInstance,
  control,
  selectedValues,
  onToggle,
  helperText,
}) => {
  const items = itemDefs.map((item) => ({
    ...item,
    value: item.value(modes),
  }));
  const isLastSelected = selectedValues.length <= 1;
  return (
    <FileWrapper>
      <FieldHead className="brand">
        <strong>{header}</strong>
        <h3>{description}</h3>
      </FieldHead>
      <GifStyleWrapper>
        {items.map((item, idx) => {
          const isSelected = selectedValues
            .map((v) => String(v))
            .includes(String(item.value));
          return (
            <MultiSelectGifStyleField
              key={idx}
              inputName={groupName}
              control={control}
              label={item.label}
              shortname={item.shortname}
              description={item.description}
              value={item.value}
              selectedTheme={selectedTheme}
              themeInstance={themeInstance}
              isSelected={isSelected}
              isLastSelected={isSelected && isLastSelected}
              onToggle={onToggle}
            />
          );
        })}
      </GifStyleWrapper>
      {helperText && (
        <p
          style={{
            marginTop: "8px",
            fontSize: "12px",
            color: "#36474F",
            opacity: 0.75,
            fontStyle: "italic",
          }}
        >
          {helperText}
        </p>
      )}
    </FileWrapper>
  );
};

// Shown beside Save: the modes failed to load (with a retry), or why the
// current selection leaves Save off. One element so aria-describedby can point
// at it.
const PhotosSaveNote = ({ modesError, retryModes, reason }) => {
  if (modesError)
    return (
      <InlineNote id="photos-save-reason" role="alert" tone="error" onRetry={retryModes}>
        Couldn't load the capture modes.
      </InlineNote>
    );
  return reason ? <InlineNote id="photos-save-reason">{reason}</InlineNote> : null;
};

const MultiSelectPhotoOptionsForm = ({
  modesError,
  retryModes,
  onSubmit,
  themeId,
  isNew,
  channelThemes,
  themeInstanceId,
  channelThemeInstances,
  edit,
  modes,
  nextTab,
  form,
  aiEnabled,
  videoEnabled,
  channelId,
  aiPersisted,
  consentAcked,
  setConsentAcked,
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { isSubmitting },
  } = form;
  const values = useWatch({ control: control });
  const enabledModeIds = Array.isArray(values["enabled_mode_ids"])
    ? values["enabled_mode_ids"]
    : [];
  const imageCustomization = values["channel_image_customization"] || {};
  const enabledImageStyles = Array.isArray(
    imageCustomization.enabled_image_styles
  )
    ? imageCustomization.enabled_image_styles
    : [];
  // The form holds the UI vocabulary internally; map "bw" coming from the
  // server (post-migration) up to "bnw" for display.
  const enabledImageStylesUi = enabledImageStyles.map(mapWireStyleToUi);
  const selectedTheme = channelThemes[themeId];
  const themeInstance = isNew ? null : channelThemeInstances[themeInstanceId];

  // Capture modes shown: hide the AI tile unless the flag is on. Image styles
  // shown: only those whose `modes` intersect the selected capture mode(s), so
  // AI reduces the list to Professional Headshot and the classic modes never
  // show it. Resolve the selected shortnames from `visibleCaptureModes` (NOT
  // the full list) so the style filter honors the flag gate too: if a channel
  // persisted the AI mode while the flag was on and it's later turned off, the
  // AI mode id no longer resolves to "ai" and the headshot style stays hidden.
  const visibleCaptureModes = captureModes.filter((mode) =>
    modeIsVisible(mode, { aiEnabled, videoEnabled })
  );
  const selectedShortnames = selectedModeShortnames(
    visibleCaptureModes,
    modes,
    enabledModeIds
  );
  const visibleStyles = visibleImageStyles(imageStyles, selectedShortnames);
  const visibleStyleTokens = visibleStyles.map((style) => style.value(modes));

  const { aiSelected, consentBlocked } = bipaConsentGate(
    selectedShortnames,
    aiPersisted,
    consentAcked
  );

  const setEnabledModeIds = (next) => {
    const normalized = next.map((v) => parseInt(v, 10)).filter((v) => !isNaN(v));
    setValue("enabled_mode_ids", normalized, { shouldDirty: true });
    // Keep the legacy mode_id in sync with the first selected mode so old
    // iPads / un-migrated Rails environments still see a sensible default.
    setValue("mode_id", normalized[0] || null, { shouldDirty: true });
  };

  const setEnabledImageStylesUi = (nextUi) => {
    const wire = nextUi.map(mapUiStyleToWire);
    const firstUi = nextUi[0];
    // Read the latest snapshot via getValues rather than spreading the
    // useWatch render-time closure — guarantees concurrent toggles still
    // preserve any other fields on channel_image_customization (e.g. id,
    // future-added keys) instead of stomping on the previous setter's write.
    const current = getValues("channel_image_customization") || {};
    setValue(
      "channel_image_customization",
      {
        ...current,
        enabled_image_styles: wire,
        is_black_and_white_mode_enabled: firstUi === UI_STYLE_BNW,
        is_glitch_mode_enabled: firstUi === "glitch",
      },
      { shouldDirty: true }
    );
  };

  const onToggleMode = (modeId, checked) => {
    const idNum = parseInt(modeId, 10);
    // Story Video is exclusive: iOS does not offer it inside a multi-mode
    // picker, so it is never combined with other modes.
    const videoId = captureModes.find((m) => m.videoMode)?.value(modes);
    if (checked && videoId != null) {
      const current = enabledModeIds.map((v) => parseInt(v, 10));
      if (idNum === videoId) {
        setEnabledModeIds([videoId]);
        return;
      }
      if (current.includes(videoId)) {
        setEnabledModeIds([idNum]);
        return;
      }
    }
    if (checked) {
      if (!enabledModeIds.map((v) => parseInt(v, 10)).includes(idNum)) {
        setEnabledModeIds([...enabledModeIds, idNum]);
      }
    } else {
      const filtered = enabledModeIds
        .map((v) => parseInt(v, 10))
        .filter((v) => v !== idNum);
      if (filtered.length === 0) return; // safety belt — shouldn't happen due to UI
      setEnabledModeIds(filtered);
    }
  };

  const onToggleStyle = (uiToken, checked) => {
    if (checked) {
      if (!enabledImageStylesUi.includes(uiToken)) {
        setEnabledImageStylesUi([...enabledImageStylesUi, uiToken]);
      }
    } else {
      const filtered = enabledImageStylesUi.filter((t) => t !== uiToken);
      if (filtered.length === 0) return;
      setEnabledImageStylesUi(filtered);
    }
  };

  // Keep the enabled image styles consistent with what the picker actually
  // shows as the capture-mode selection changes: drop any now-hidden style, and
  // if that empties the selection auto-select the first visible style. When AI
  // becomes the only mode this reduces the list to Professional Headshot and
  // selects it, satisfying the >=1-style save requirement.
  const visibleStyleKey = visibleStyleTokens.join("|");
  const enabledStyleKey = enabledImageStylesUi.join("|");
  useEffect(() => {
    const pruned = enabledImageStylesUi.filter((token) =>
      visibleStyleTokens.includes(token)
    );
    const next =
      pruned.length > 0 ? pruned : visibleStyleTokens.slice(0, 1);
    const changed =
      next.length !== enabledImageStylesUi.length ||
      next.some((token, i) => token !== enabledImageStylesUi[i]);
    if (changed) setEnabledImageStylesUi(next);
    // Keyed by the joined token lists so the effect settles once state matches
    // the visible set (setEnabledImageStylesUi is stable within a render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleStyleKey, enabledStyleKey]);

  const saveDisabled = isSaveDisabled({
    isSubmitting,
    enabledModeIds,
    enabledImageStylesUi,
    consentBlocked,
  });
  const photosReason = saveBlockReason({ enabledModeIds, enabledImageStylesUi });
  const describedBy = modesError || photosReason ? "photos-save-reason" : undefined;

  return (
    <StyledForm
      onSubmit={handleSubmit(onSubmit)}
      className="channel_forms--photo-options"
    >
      <PhotoOptionsWrapper>
        <MultiSelectPhotoOptionsSection
          groupName="enabled_mode_ids"
          header="Capture Mode"
          description="Select the capture modes you’d like to offer for this channel."
          itemDefs={visibleCaptureModes}
          selectedTheme={selectedTheme}
          themeInstance={themeInstance}
          modes={modes}
          selectedValues={enabledModeIds}
          onToggle={onToggleMode}
          control={control}
          helperText={sectionHelperText(enabledModeIds.length)}
        />
        <Divider marginBottom={20}></Divider>
        <MultiSelectPhotoOptionsSection
          groupName="enabled_image_styles"
          header="Image Style"
          description="Select the image styles you’d like to offer for this channel."
          itemDefs={visibleStyles}
          selectedTheme={selectedTheme}
          themeInstance={themeInstance}
          modes={modes}
          selectedValues={enabledImageStylesUi}
          onToggle={onToggleStyle}
          control={control}
          helperText={sectionHelperText(enabledImageStylesUi.length)}
        />
        {aiSelected && (
          <BipaConsentNotice
            channelId={channelId}
            showAck={!aiPersisted}
            acked={consentAcked}
            onAckChange={setConsentAcked}
          />
        )}
      </PhotoOptionsWrapper>
      <Divider marginBottom={48}></Divider>
      <UpgradeText className="left-pos pdd-20">
        Looking for more? Send us a suggestion.
      </UpgradeText>
      <div className="channel-forms__group">
        {edit ? (
          <Button type="submit" aria-describedby={describedBy} disabled={saveDisabled}>
            Save
          </Button>
        ) : (
          <Button
            type="button"
            onClick={() => nextTab()}
            aria-describedby={describedBy}
            disabled={saveDisabled}
          >
            Save & continue
          </Button>
        )}
        <PhotosSaveNote
          modesError={modesError}
          retryModes={retryModes}
          reason={photosReason}
        />
      </div>
    </StyledForm>
  );
};

// === Legacy single-select (flag OFF / default) variant =========================

const SingleSelectGifStyleField = ({
  label,
  selectedTheme,
  themeInstance,
  value,
  shortname,
  name,
  description,
  selectedValue,
  control,
  setValue,
}) => {
  const values = useWatch({ control: control });
  const firstLine = values["first_line_text"];
  const secondLine = values["second_line_text"];
  const withGraphic = values["with_photo_filter_graphic"];
  const withFilter = values["with_photo_filter"];
  return (
    <GifStyleFieldLabel
      // Mirror the radio's `checked` comparison (value == selectedValue) so the
      // tile highlight tracks selection for BOTH rows. The previous
      // parseInt(value) === parseInt(modeId) check produced NaN for the string
      // image-style tokens ("standard"/"bnw"/"glitch"), so the image-style tile
      // never received the "selected" class even when its radio was checked.
      className={value == selectedValue ? "selected" : ""}
    >
      <PhotoFilterPreview
        med
        selectedTheme={selectedTheme}
        themeInstance={themeInstance}
        noMargin={true}
        center={true}
        photoOptions={shortname}
        firstLine={firstLine}
        secondLine={secondLine}
        withGraphic={withGraphic}
        withFilter={withFilter}
      />
      <RadioInputWrapper>
        <div className=" text-mb-body flex items-center gap-3 pl-1 py-.05">
          <Controller
            control={control}
            name={name}
            render={({ field: { onChange, ...fieldRest } }) => (
              <input
                {...fieldRest}
                checked={value == selectedValue}
                onChange={(e) => {
                  const val = e.target.value;
                  if (!value) {
                    onChange(null);
                    return;
                  }
                  if (name === "mode_id") {
                    // Write BOTH the legacy scalar and the multi-select array:
                    // the wire serializer prefers a present `enabled_mode_ids`,
                    // so writing only `mode_id` left the server-seeded array in
                    // place and the save carried the OLD mode (prod incident
                    // 2026-09-10). See singleSelectModePatch.
                    const patch = singleSelectModePatch(val);
                    onChange(patch.mode_id);
                    setValue("enabled_mode_ids", patch.enabled_mode_ids, {
                      shouldDirty: true,
                    });
                    return;
                  }
                  // Preserve the existing customization record id across style
                  // changes and write the chosen style to BOTH the legacy
                  // booleans and `enabled_image_styles` (see
                  // singleSelectStylePatch for why both sides are required).
                  onChange(
                    singleSelectStylePatch(
                      values["channel_image_customization"],
                      val
                    )
                  );
                }}
                value={value}
                type="radio"
              />
            )}
          />
          <label>{label}</label>
        </div>
        <Tooltip
          offset="-10px"
          width="200px"
          position="bottom"
          text={description}
        >
          <Icon size="12" name="help" />
        </Tooltip>
      </RadioInputWrapper>
    </GifStyleFieldLabel>
  );
};

const SingleSelectPhotoOptionsSection = ({
  groupName,
  header,
  description,
  // The already-filtered definitions to render (capture modes gated by the AI
  // flag, image styles gated by the selected capture mode).
  itemDefs,
  modes,
  ...props
}) => (
  <FileWrapper>
    {/* Header rendered via FieldHead per the brand pattern; LargeLabel +
        DescriptionLabel imports retained for parity with downstream forms. */}
    <FieldHead className="brand">
      <strong>{header}</strong>
      <h3>{description}</h3>
    </FieldHead>
    <GifStyleWrapper>
      {itemDefs.map((item, idx) => (
        <SingleSelectGifStyleField
          key={idx}
          name={groupName}
          {...props}
          {...item}
          value={item.value(modes)}
        />
      ))}
    </GifStyleWrapper>
  </FileWrapper>
);

const SingleSelectPhotoOptionsForm = ({
  modesError,
  retryModes,
  onSubmit,
  themeId,
  isNew,
  channelThemes,
  themeInstanceId,
  channelThemeInstances,
  edit,
  modes,
  nextTab,
  form,
  aiEnabled,
  videoEnabled,
  channelId,
  aiPersisted,
  consentAcked,
  setConsentAcked,
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = form;
  const values = useWatch({ control: control });
  const modeId = values["mode_id"];
  const imageCustomization = values["channel_image_customization"];
  const selectedTheme = channelThemes[themeId];
  const themeInstance = isNew ? null : channelThemeInstances[themeInstanceId];

  // Capture modes shown: hide the AI tile unless the flag is on. Image styles
  // shown: only those whose `modes` intersect the selected capture mode, so AI
  // reduces the list to Professional Headshot and the classic modes never
  // show it. Resolve the selected shortname from `visibleCaptureModes` (NOT the
  // full list) so the style filter honors the flag gate too: a channel that
  // persisted the AI mode while the flag was on won't leak the headshot style
  // once the flag is turned off.
  const visibleCaptureModes = captureModes.filter((mode) =>
    modeIsVisible(mode, { aiEnabled, videoEnabled })
  );
  const selectedShortnames = selectedModeShortnames(
    visibleCaptureModes,
    modes,
    modeId != null ? [modeId] : []
  );
  const visibleStyles = visibleImageStyles(imageStyles, selectedShortnames);
  const currentStyleValue = singleSelectStyleValue(imageCustomization);
  const { aiSelected, consentBlocked } = bipaConsentGate(
    selectedShortnames,
    aiPersisted,
    consentAcked
  );
  // Whether the only style the current mode allows is the AI headshot — used to
  // auto-select it even on a channel that had no style chosen yet.
  const aiOnlyStyle =
    visibleStyles.length > 0 &&
    visibleStyles.every((style) => style.value(modes) === HEADSHOT_STYLE_VALUE);

  // When the capture mode changes such that the current image style is no
  // longer offered, snap to the first visible style. Guarded so a brand-new
  // channel with no style chosen keeps the legacy "pick manually" behavior
  // UNLESS AI forces the single headshot option.
  const visibleStyleKey = visibleStyles
    .map((style) => style.value(modes))
    .join("|");
  useEffect(() => {
    if (visibleStyles.length === 0) return;
    const stillVisible = visibleStyles.some(
      (style) => style.value(modes) === currentStyleValue
    );
    if (stillVisible) return;
    if (currentStyleValue === "" && !aiOnlyStyle) return;
    const first = visibleStyles[0];
    setValue(
      "channel_image_customization",
      singleSelectStylePatch(imageCustomization, first.value(modes)),
      { shouldDirty: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleStyleKey, currentStyleValue, aiOnlyStyle]);

  return (
    <StyledForm
      onSubmit={handleSubmit(onSubmit)}
      className="channel_forms--photo-options"
    >
      <PhotoOptionsWrapper>
        <SingleSelectPhotoOptionsSection
          groupName="mode_id"
          header="Capture Mode"
          description="Select the capture mode you’d like to use for this channel."
          itemDefs={visibleCaptureModes}
          selectedTheme={selectedTheme}
          themeInstance={themeInstance}
          modes={modes}
          selectedValue={modeId}
          control={control}
          setValue={setValue}
        />
        <Divider marginBottom={20}></Divider>
        <SingleSelectPhotoOptionsSection
          groupName="channel_image_customization"
          header="Image Style"
          description="Select a style to apply to the images captured."
          itemDefs={visibleStyles}
          selectedTheme={selectedTheme}
          themeInstance={themeInstance}
          modes={modes}
          selectedValue={currentStyleValue}
          control={control}
        />
        {aiSelected && (
          <BipaConsentNotice
            channelId={channelId}
            showAck={!aiPersisted}
            acked={consentAcked}
            onAckChange={setConsentAcked}
          />
        )}
      </PhotoOptionsWrapper>
      <Divider marginBottom={48}></Divider>
      <UpgradeText className="left-pos pdd-20">
        Looking for more? Send us a suggestion.
      </UpgradeText>
      <div className="channel-forms__group">
        {edit ? (
          <Button
            type="submit"
            aria-describedby={modesError || !modeId ? "photos-save-reason" : undefined}
            disabled={isSubmitting || !modeId || consentBlocked}
          >
            Save
          </Button>
        ) : (
          <Button
            type="button"
            onClick={() => nextTab()}
            aria-describedby={modesError || !modeId ? "photos-save-reason" : undefined}
            disabled={isSubmitting || !modeId || consentBlocked}
          >
            Save & continue
          </Button>
        )}
        <PhotosSaveNote
          modesError={modesError}
          retryModes={retryModes}
          reason={modeId ? null : "Pick a capture mode."}
        />
      </div>
    </StyledForm>
  );
};

// === Top-level form: pick variant by feature flag ==============================

const PhotoOptionsForm = (props) => {
  // Default-off contract: until the SDK has loaded and the flag has resolved
  // to true, render the legacy single-select UI. iOS in-field readiness gates
  // the flag rollout — see MULTI_SELECT_FLAG comment above.
  const multiSelectEnabled = useFeatureFlag(MULTI_SELECT_FLAG);
  // Independent flag — the AI mode/style rides whichever picker variant is
  // active. Default-off / fail-closed like every flag read here.
  const aiEnabled = useFeatureFlag(AI_MODE_FLAG);
  // Story Video mirrors rails' write gate (StoryVideoModeGate): a superadmin
  // always sees it; otherwise the flag counts only when the signed-in user owns
  // the channel, because rails checks the OWNER's flag and PostHog evaluates
  // the signed-in user's (`ownsChannel`, computed by Forms). A channel already
  // saved in video mode keeps the tile, as rails keeps accepting its saves;
  // hiding it would also strip the saved mode from the style lookup and blank
  // the channel's image styles.
  const storyVideoFlag = useFeatureFlag(STORY_VIDEO_FLAG);
  const isSuperAdmin = useSelector(
    (state) => state.app?.user?.permission_role === "super_admin"
  );
  const videoPersisted = videoModeSelected(
    selectedModeShortnames(captureModes, props.modes, props.persistedModeIds)
  );
  const videoEnabled =
    isSuperAdmin ||
    videoPersisted ||
    (storyVideoFlag && props.ownsChannel === true);

  // BIPA consent gate state lives HERE, above the variant switch, so a
  // late-resolving multi-select flag (which swaps the variant and remounts it)
  // can neither reset nor spoof it. `aiPersisted` is derived from the
  // channel's server-seeded `persistedModeIds` (Forms threads
  // initialValues → this prop; empty for a new channel) against the FULL
  // captureModes list, so an AI channel is recognized as persisted regardless
  // of the flag's current value. The acknowledgment is session-local UI
  // state, deliberately not a form field: nothing about it goes on the
  // channel-save wire (rails' hard consent gate lives on AI-headshot
  // creation, not channel save).
  const aiPersisted = aiModeSelected(
    selectedModeShortnames(captureModes, props.modes, props.persistedModeIds)
  );
  // The acknowledgment is owned by Forms/index.js and passed down, so the
  // shared-submit guard there consults the SAME state this tab's checkbox
  // sets — a sibling tab's Save cannot persist an unacknowledged AI mode.
  // Standalone renders (tests) fall back to local state.
  const [localConsentAcked, setLocalConsentAcked] = useState(false);
  const consentAcked = props.consentAcked ?? localConsentAcked;
  const setConsentAcked = props.setConsentAcked ?? setLocalConsentAcked;
  const gateProps = {
    aiEnabled,
    videoEnabled,
    aiPersisted,
    consentAcked,
    setConsentAcked,
  };
  return multiSelectEnabled ? (
    <MultiSelectPhotoOptionsForm {...props} {...gateProps} />
  ) : (
    <SingleSelectPhotoOptionsForm {...props} {...gateProps} />
  );
};

const mapStateToProps = createStructuredSelector({
  modesError: selectModesError(),
});

const mapDispatchToProps = (dispatch) => ({
  goToThemeTab: () => dispatch(changeTab(1)),
  retryModes: () => dispatch(fetchModesRequest()),
});

// Exported for tests so they can render variants directly without standing
// up the full ChannelsPage redux subtree the connect() wrapper expects.
export {
  PhotoOptionsForm as PhotoOptionsFormUnconnected,
  MultiSelectPhotoOptionsForm,
  SingleSelectPhotoOptionsForm,
};

export default connect(mapStateToProps, mapDispatchToProps)(PhotoOptionsForm);
